import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { defaultInstance } from "../api/httpClient";
import { toaster } from "baseui/toast";
import type { Business, BusinessResponse } from "./useBusinessAccessType";

export type { Business };

// Hook to fetch all businesses (admin view)
export const useAllBusinessesCrud = () => {
  return useQuery<Business[], Error>({
    queryKey: ['businesses-crud'],
    queryFn: async () => {
      const response = await defaultInstance<BusinessResponse>({
        url: '/business',
        method: 'GET',
      });

      const apiResponse = response.data;
      if (Array.isArray(apiResponse)) return apiResponse;
      return Array.isArray((apiResponse as any).data) ? (apiResponse as any).data : [];
    },
  });
};

// Hook to fetch a single business by ID (non-admin view)
export const useBusinessById = (businessId: string | null) => {
  return useQuery<Business[], Error>({
    queryKey: ['businesses-crud', businessId],
    queryFn: async () => {
      if (!businessId) throw new Error('Business ID is required');

      const response = await defaultInstance<BusinessResponse>({
        url: `/business/${businessId}`,
        method: 'GET',
      });

      const apiResponse = response.data;
      if (Array.isArray(apiResponse)) return apiResponse;
      if ((apiResponse as any).data && Array.isArray((apiResponse as any).data)) return (apiResponse as any).data;
      // Single object response
      if (apiResponse && typeof apiResponse === 'object' && !Array.isArray(apiResponse)) {
        const data = (apiResponse as any).data;
        if (data && !Array.isArray(data)) return [data];
        return data ? data : [apiResponse];
      }
      return [];
    },
    enabled: !!businessId,
  });
};

// Create business mutation
export const useCreateBusiness = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Business>) => {
      return await defaultInstance({
        url: '/business',
        method: 'POST',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses-crud'] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      toaster.positive('Business created successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to create business', { autoHideDuration: 3000 });
    },
  });
};

// Update business mutation
export const useUpdateBusiness = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Business> }) => {
      return await defaultInstance({
        url: `/business/${id}`,
        method: 'PATCH',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses-crud'] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      toaster.positive('Business updated successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to update business', { autoHideDuration: 3000 });
    },
  });
};

// Delete business mutation
export const useDeleteBusiness = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await defaultInstance({
        url: `/business/${id}`,
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses-crud'] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      toaster.positive('Business deleted successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to delete business', { autoHideDuration: 3000 });
    },
  });
};

// ---- Business classification taxonomy (SAM-163) ----

export interface BusinessCategory {
  id: number;
  name: string;
  priority?: number;
  /** Stable key (`estate` = the Estate category, SAM-1298). */
  key?: string | null;
}

export interface BusinessType {
  id: number;
  name: string;
  business_category_id: number;
}

const unwrapList = <T,>(apiResponse: any): T[] => {
  if (Array.isArray(apiResponse)) return apiResponse;
  return Array.isArray(apiResponse?.data) ? apiResponse.data : [];
};

// Categories ("Category" select). Sorted by priority, then name.
export const useBusinessCategories = () => {
  return useQuery<BusinessCategory[], Error>({
    queryKey: ['business-categories'],
    queryFn: async () => {
      const response = await defaultInstance({ url: '/business_category?limit=100', method: 'GET' });
      const list = unwrapList<BusinessCategory>(response.data);
      return [...list].sort(
        (a, b) => (a.priority ?? 999) - (b.priority ?? 999) || a.name.localeCompare(b.name),
      );
    },
    staleTime: 5 * 60 * 1000,
  });
};

// Types ("Subcategory" select) — filtered client-side by business_category_id.
export const useBusinessTypes = () => {
  return useQuery<BusinessType[], Error>({
    queryKey: ['business-types'],
    queryFn: async () => {
      const response = await defaultInstance({ url: '/business_type?limit=500', method: 'GET' });
      const list = unwrapList<BusinessType>(response.data);
      return [...list].sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 5 * 60 * 1000,
  });
};
